import {
  create,
  ApisauceInstance,
  ApiResponse,
  ApisauceConfig,
} from 'apisauce';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { THOM_BASE_URL, USER_BASE_URL } from '../services/Constant';
import { RefreshTokenResponse } from '../utils/type';
import {
  checkIfOfflineIsAddedByReferralId,
  getCustomParamsApis,
  getProgramBasedOnReferralId,
  getResponse,
  isExcludeCommonParamsApis,
  isIncludeParamsApis,
  isOfflinePostApis,
  saveResponsePayload,
} from './Offline';
import { ConsentDetailsType } from './configApi/forms/Consent/ConsentServices';
import { isExistInDB } from './storageDB';
import {
  getSavedTime,
  isOlderThanFiveHours,
  saveCurrentTime,
} from '../utils/utilities';

export default class ThomApiClient {
  private static _instance: ThomApiClient;

  private static abortControllers: AbortController[] = []; // Track all request controllers

  // Shared refresh promise to prevent multiple parallel refreshes
  private static refreshPromise: Promise<string | void> | null = null;

  private readonly api: ApisauceInstance;

  private static isLogoutTriggered: boolean = false;

  constructor() {
    this.api = create({
      baseURL: THOM_BASE_URL,
    });

    // Request interceptor: if session is expired, short-circuit with a 403.
    // This will be handled by the existing response interceptor logic.
    this.api.axiosInstance.interceptors.request.use((config) => {
      try {
        if (ThomApiClient.shouldExpireSession()) {
          return Promise.reject(
            ThomApiClient.makeAxios403Error({
              code: 1045,
              message: 'Access token expired',
            }),
          );
        }
      } catch (e) {
        // In case of any parsing error, default to forcing a 403 to be safe.
        return Promise.reject(
          ThomApiClient.makeAxios403Error({
            code: 1045,
            message: 'Access token expired',
          }),
        );
      }
      return config;
    });

    this.api.addRequestTransform((request) => {
      const requestConfig = request;
      if (requestConfig?.headers) {
        requestConfig.headers = {
          ...requestConfig.headers,
          appSource: `TCS`,
          Authorization: `Bearer ${localStorage?.getItem('accessToken')}`,
        };
      }

      const controller = new AbortController();
      request.signal = controller.signal;
      ThomApiClient.abortControllers.push(controller);

      return requestConfig;
    });

    this.api.axiosInstance.interceptors.response.use(
      (response) => {
        ThomApiClient.cleanupAbortController(response?.config?.signal);
        return response;
      },
      async (error) => {
        const originalConfig = error.config;
        if (
          error.response &&
          error.response.status == 403 &&
          (error.response.data.code == 1045 || error.response.data.code == 1039)
        ) {
          if (error.response.data.code == 1039) {
            localStorage.setItem('permissionDenied', 'true');
          }
          throw error;
        } else if (error.response && error.response.status == 403) {
          try {
            // Shared refresh logic: only one refresh at a time
            if (!ThomApiClient.refreshPromise) {
              ThomApiClient.refreshPromise = (async () => {
                const api = create({ baseURL: USER_BASE_URL });
                const response = await api.post('login', {
                  refreshToken: localStorage.getItem('refreshToken'),
                  grantType: 'refresh_token',
                });
                if (response.ok) {
                  const data = response.data as RefreshTokenResponse;
                  localStorage.setItem('accessToken', data.accessToken);
                  localStorage.setItem('refreshToken', data.refreshToken);
                  return data.accessToken;
                } else {
                  ThomApiClient.cancelAllRequests();
                  throw response.originalError;
                }
              })();
              ThomApiClient.refreshPromise.finally(() => {
                ThomApiClient.refreshPromise = null;
              });
            }

            const newToken = await ThomApiClient.refreshPromise;
            originalConfig.headers.Authorization = `Bearer ${newToken}`;
            return await this.api.axiosInstance.request(originalConfig);
          } catch (err) {
            if (!ThomApiClient.isLogoutTriggered) {
              ThomApiClient.isLogoutTriggered = true;
              throw err;
            }
          }
        } else {
          throw error;
        }
      },
    );
  }

  /**
   * Remove a controller from the array after request completes.
   */
  private static cleanupAbortController(signal: AbortSignal | undefined) {
    if (!signal) return;
    ThomApiClient.abortControllers = ThomApiClient.abortControllers.filter(
      (controller) => controller.signal !== signal,
    );
  }

  /**
   * Cancel all pending API requests by aborting their controllers.
   */
  public static cancelAllRequests() {
    ThomApiClient.abortControllers.forEach((controller) => controller.abort());
    ThomApiClient.abortControllers = [];
  }

  public static getInstance(): ThomApiClient {
    return this._instance ?? new ThomApiClient();
  }

  appendProgramIdQueryParam = (url: string) => {
    const selectedRefferalId: string | null =
      localStorage.getItem('referralId');
    const programId = getProgramBasedOnReferralId(selectedRefferalId);
    const zoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (programId) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}programId=${programId}&referralId=${
        selectedRefferalId || ''
      }&zoneName=${zoneName}`;
    } else {
      return `${url}&zoneName=${zoneName}`;
    }
  };

  async post<T, U>(url: string, payload?: T): Promise<ApiResponse<T, U>> {
    const urlType = this.appendProgramIdQueryParam(url).split('?')[0];
    const savedUrl = getSavedUrl(this.appendProgramIdQueryParam(url));

    if (isOfflinePostApis(urlType) || (await isExistInDB(savedUrl))) {
      const saved = await saveResponsePayload(
        'post',
        this.api.getBaseURL(),
        this.appendProgramIdQueryParam(url),
        payload,
      );
      if (saved) {
        return { ok: true, data: {}, status: 200 } as ApiResponse<T, U>;
      }
    }
    const response = await this.api.post<T, U>(
      this.appendProgramIdQueryParam(url),
      payload,
    );
    return response;
  }

  async get<T>(
    url: string,
    params?: object,
    config?: ApisauceConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.api.get<T>(
      this.appendProgramIdQueryParam(url),
      params,
      config,
    );
    const urlKey =
      this.api.getBaseURL() +
      this.appendProgramIdQueryParam(url) +
      (typeof params === 'string' ? params : '');

    const urlType = this.appendProgramIdQueryParam(url).split('?')[0];
    const paramsUrl = this.appendProgramIdQueryParam(url).split('?')[1];
    const paramsget = paramsUrl.split('&');
    const savedurl = getSavedUrl(this.appendProgramIdQueryParam(url));

    let offlineAvailable = await isExistInDB(savedurl);
    if (!response.ok) {
      offlineAvailable = true;
    }

    if (urlType === 'consent') {
      const urltemp = new URL(urlKey);
      const referralId = urltemp.searchParams.get('referralId') ?? '';
      await checkIfOfflineIsAddedByReferralId(referralId).then((res) => {
        if (res) {
          offlineAvailable = true;
        }
      });
    }

    if (
      savedurl === 'clients?' + paramsget[paramsget.length - 2] &&
      response.ok
    ) {
      offlineAvailable = false;
    }

    if (
      offlineAvailable &&
      (!response.ok || (await isExistInDB(savedurl)) || urlType === 'consent')
    ) {
      let urlget;
      if (isExcludeCommonParamsApis(urlType)) {
        urlget = urlType + '?' + paramsget[paramsget.length - 2];
      } else if (isIncludeParamsApis(urlType)) {
        urlget = urlType + '?' + paramsUrl;
      } else {
        urlget =
          urlType +
          '?' +
          paramsget[paramsget.length - 2] +
          '&' +
          paramsget[paramsget.length - 1];
      }

      if (urlType === 'employee/listByRole') {
        urlget =
          getCustomParamsApis(urlKey) + '&' + paramsget[paramsget.length - 2];
      }

      let cachedData = (await getResponse(urlget)) as T;
      console.log('cachedData for ' + urlget, cachedData);
      if (cachedData) {
        return { ok: true, data: cachedData } as ApiResponse<T>;
      } else {
        let consentType = '' as string | null;
        if (urlType === 'consent') {
          const urltemp = new URL(urlKey);
          consentType = urltemp.searchParams.get('type');

          if (consentType === 'PriorWrittenNoticeForm') {
            urlget =
              'defaultEmployeeConsent?' +
              paramsget[paramsget.length - 2] +
              '&' +
              paramsget[paramsget.length - 1];
          } else {
            urlget =
              'defaultClientConsent?' +
              paramsget[paramsget.length - 2] +
              '&' +
              paramsget[paramsget.length - 1];
          }
        }
        cachedData = (await getResponse(urlget)) as T & {
          type: ConsentDetailsType;
        };
        if (urlType === 'consent' && consentType) {
          (cachedData as ConsentDetailsType).type = consentType;
          (cachedData as ConsentDetailsType).consentId = '';
        }
        if (cachedData) {
          return { ok: true, data: cachedData } as ApiResponse<T>;
        }
      }
    } else {
      // udpate the obj if already exist(handled in offline file)
    }
    return response;
  }

  async put<T, U>(url: string, payload?: T): Promise<ApiResponse<T, U>> {
    const urlType = this.appendProgramIdQueryParam(url).split('?')[0];
    const savedUrl = getSavedUrl(this.appendProgramIdQueryParam(url));

    if (isOfflinePostApis(urlType) || (await isExistInDB(savedUrl))) {
      const saved = await saveResponsePayload(
        'put',
        this.api.getBaseURL(),
        this.appendProgramIdQueryParam(url),
        payload,
      );
      if (saved) {
        return { ok: true, data: {}, status: 200 } as ApiResponse<T, U>;
      }
    }
    const response = await this.api.put<T, U>(
      this.appendProgramIdQueryParam(url),
      payload,
    );
    return response;
  }

  async delete<T>(url: string): Promise<ApiResponse<T, T>> {
    const urlType = this.appendProgramIdQueryParam(url).split('?')[0];
    if (urlType === 'clients/communicationLogs') {
      const savedurl = getSavedUrl(this.appendProgramIdQueryParam(url));

      const offlineAvailable = await isExistInDB(savedurl);
      if (offlineAvailable) {
        return { ok: false, data: {}, status: 400 } as ApiResponse<T>;
      }
    }
    const response = this.api.delete<T>(this.appendProgramIdQueryParam(url));
    return response;
  }

  async patch<T, U>(url: string, payload?: T): Promise<ApiResponse<T, U>> {
    const response = await this.api.patch<T, U>(
      this.appendProgramIdQueryParam(url),
      payload,
    );

    return response;
  }

  // === Session expiration helpers ===
  private static shouldExpireSession(): boolean {
    const saved = getSavedTime();
    const forceLogout = !saved || isOlderThanFiveHours();
    if (!forceLogout) {
      saveCurrentTime();
    }
    return forceLogout;
  }

  private static makeAxios403Error(data: { code: number; message?: string }) {
    const cfg: AxiosRequestConfig = {};
    const resp: AxiosResponse = {
      data,
      status: 403,
      statusText: 'Forbidden',
      headers: {},
      config: cfg,
      request: {},
    };
    const err = new Error(data?.message ?? 'Forbidden') as Error & {
      config: AxiosRequestConfig;
      response: AxiosResponse;
      isAxiosError: true;
      toJSON: () => unknown;
    };
    err.config = cfg;
    err.response = resp;
    err.isAxiosError = true;
    err.toJSON = () => ({
      message: data?.message ?? 'Forbidden',
      code: data.code,
    });
    return err;
  }
}

const getSavedUrl = (url: string) => {
  const urlType = url.split('?')[0];
  const paramsUrl = url.split('?')[1];
  const params = paramsUrl.split('&');
  const separator = urlType.includes('?') ? '&' : '?';
  let urlKey;
  if (isExcludeCommonParamsApis(urlType)) {
    urlKey = urlType + separator + params[params.length - 2];
  } else if (isIncludeParamsApis(urlType)) {
    urlKey = urlType + '?' + paramsUrl;
  } else {
    urlKey =
      urlType +
      '?' +
      params[params.length - 2] +
      '&' +
      params[params.length - 1];
  }
  return urlKey;
};
